/**
 * MobilePlayerFullscreen Component
 *
 * Full-screen mobile player interface inspired by Audible's design.
 * Features:
 * - Large album artwork
 * - Chapter navigation
 * - Progress bar with timestamps
 * - Large centered playback controls
 * - Bottom utility bar (speed, chapters, timer, chat, share)
 *
 * This component is designed to work in portrait orientation on mobile devices.
 */

import {
	ChevronDown,
	MessageSquare,
	List,
	Gauge,
	X,
	Plus,
	Minus,
	FileText,
	Image,
	Globe,
	CheckCircle2,
	Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
	usePlayerStore,
	playerSelectors,
	getPlayerActions,
} from "@/lib/player/player.store";
import type { MediaPlayerInstance } from "@vidstack/react";
import { PlayerControls } from "./playerControls.component";
import { ShareButton } from "@/features/podcast/components/shareButton.component";
import { RegisterPromptDialog } from "@/components/registerPromptDialog.component";
import { useAuth } from "@/lib/contexts/auth.context";
import { useEffect, useRef, useMemo, useState, memo } from "react";
import { usePlayerGestures } from "./hooks/usePlayerGestures.hook";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PLAYBACK_SPEED_OPTIONS } from "./player.constants";
import {
	useTranscript,
	findActiveLine,
	expandSegmentToLines,
	type TranscriptLine,
} from "./hooks/useTranscript.hook";
import { TranscriptLineRow } from "./transcriptLineRow.component";
import type { TranscriptChapter } from "@/lib/podcast/podcast.service";
import { PodcastService } from "@/lib/podcast/podcast.service";
import {
	useTranscriptStore,
	getTranscriptActions,
} from "@/lib/transcript/transcript.store";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

const TRANSLATABLE_LANGUAGES: { code: string; label: string }[] = [
	{ code: "en", label: "English" },
	{ code: "de", label: "Deutsch" },
	{ code: "fr", label: "Français" },
	{ code: "es", label: "Español" },
	{ code: "it", label: "Italiano" },
	{ code: "pt", label: "Português" },
	{ code: "nl", label: "Nederlands" },
	{ code: "pl", label: "Polski" },
	{ code: "ru", label: "Русский" },
	{ code: "ja", label: "日本語" },
	{ code: "zh", label: "中文" },
	{ code: "ko", label: "한국어" },
	{ code: "tr", label: "Türkçe" },
	{ code: "sv", label: "Svenska" },
];

interface MobileTranscriptChapterProps {
	chapter: TranscriptChapter;
	chapterIndex: number;
	activeSegmentIndex: number | null;
	activeLineIndex: number | null;
	activeLineRef: React.RefObject<HTMLDivElement | null>;
	isDualLanguage: boolean;
	isTranslating: boolean;
	translationLangCode: string;
}

const MobileTranscriptChapter = memo(function MobileTranscriptChapter({
	chapter,
	chapterIndex,
	activeSegmentIndex,
	activeLineIndex,
	activeLineRef,
	isDualLanguage,
	isTranslating,
	translationLangCode,
}: MobileTranscriptChapterProps) {
	const allLines = useMemo(() => {
		const result: Array<{
			line: TranscriptLine;
			segmentIndex: number;
			lineIndex: number;
		}> = [];
		chapter.segments.forEach((seg, si) => {
			expandSegmentToLines(
				seg,
				isDualLanguage ? translationLangCode : undefined,
			).forEach((line, li) => {
				result.push({ line, segmentIndex: si, lineIndex: li });
			});
		});
		return result;
	}, [chapter.segments, isDualLanguage, translationLangCode]);

	return (
		<>
			{allLines.map(({ line, segmentIndex, lineIndex }) => {
				const isActive =
					activeSegmentIndex === segmentIndex && activeLineIndex === lineIndex;
				return (
					<TranscriptLineRow
						key={`${chapterIndex}-${segmentIndex}-${lineIndex}`}
						line={line}
						isActive={isActive}
						isDualLanguage={isDualLanguage}
						isTranslating={isTranslating}
						lineRef={isActive ? activeLineRef : undefined}
					/>
				);
			})}
		</>
	);
});

interface MobilePlayerFullscreenProps {
	playerRef: React.RefObject<MediaPlayerInstance | null>;
	onMinimize: () => void;
	onOpenChat: () => void;
	onOpenChapters: () => void;
}

export function MobilePlayerFullscreen({
	playerRef,
	onMinimize,
	onOpenChat,
	onOpenChapters,
}: MobilePlayerFullscreenProps) {
	const { isAuthenticated } = useAuth();
	const { t, i18n } = useTranslation();
	const [isSpeedPopupOpen, setIsSpeedPopupOpen] = useState(false);
	const [isExiting, setIsExiting] = useState(false);
	const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
	const [showTranscript, setShowTranscript] = useState(false);
	const [showMobileTranslateControls, setShowMobileTranslateControls] =
		useState(false);
	const [mobileGenerateLang, setMobileGenerateLang] = useState<string>(
		i18n.language.split("-")[0] ?? "en",
	);
	const isPaused = usePlayerStore((state) => state.isPaused);
	const currentTime = usePlayerStore((state) => state.currentTime);
	const duration = usePlayerStore((state) => state.duration);
	const playbackSpeed = usePlayerStore((state) => state.playbackSpeed);
	const chapters = usePlayerStore((state) => state.chapters);
	const activeChapterIndex = usePlayerStore(
		(state) => state.activeChapterIndex,
	);
	const formatTime = usePlayerStore((state) => state.formatTime);
	const currentMedia = usePlayerStore((state) => state.currentMedia);
	const podcastChapters = usePlayerStore((state) => state.podcastChapters);
	const podWorkspaceId = usePlayerStore((state) => state.podWorkspaceId);
	const podcastLanguage = usePlayerStore(
		(state) => state.podcastChapters[0]?.language ?? "de",
	);
	const podcastLang = podcastLanguage.split("-")[0] ?? podcastLanguage;
	const originalLang = podcastLang.slice(0, 2).toUpperCase();
	const podcastId = usePlayerStore((state) => state.podcastId);

	// ── Translation state (from store — survives component unmount) ─────────
	// Must come after podcastId is declared since the selector depends on it
	const isDualLanguage = useTranscriptStore((s) => s.isDualLanguage);
	const translationLangCode = useTranscriptStore((s) => s.translationLangCode);
	const isTranslatingLang = useTranscriptStore((s) =>
		podcastId ? (s.translatingByPodcast[podcastId] ?? null) : null,
	);

	const currentChapter = playerSelectors.getCurrentChapter(
		usePlayerStore.getState(),
	);

	// Transcript — polling happens automatically when the store marks a job in progress
	const { data: transcript, isLoading: transcriptLoading } =
		useTranscript(podcastId);

	// Detect translation completion
	useEffect(() => {
		if (!isTranslatingLang || !transcript || !podcastId) return;
		const segments = transcript.chapters.flatMap((ch) => ch.segments);
		if (segments.length === 0) return;
		const allDone = segments.every(
			(seg) => !!seg.translations?.[isTranslatingLang],
		);
		if (allDone) {
			getTranscriptActions().completeTranslation(podcastId, isTranslatingLang);
			setShowMobileTranslateControls(false);
		}
	}, [transcript, isTranslatingLang, podcastId]);

	// Available translations for this podcast
	const availableTranslations = useMemo<string[]>(() => {
		if (!transcript) return [];
		const segments = transcript.chapters.flatMap((ch) => ch.segments);
		if (segments.length === 0) return [];
		return [
			...new Set(
				segments.flatMap((seg) => Object.keys(seg.translations ?? {})),
			),
		].filter((lang) => lang !== podcastLang);
	}, [transcript, podcastLang]);
	const activeLine = useMemo(
		() => findActiveLine(transcript, currentTime),
		[transcript, currentTime],
	);
	const activeSegRef = useRef<HTMLDivElement | null>(null);
	const prevShowTranscriptRef = useRef(false);

	// Scroll to current line when transcript is toggled on, or active line changes
	useEffect(() => {
		if (!showTranscript || !activeSegRef.current) return;
		const justOpened = !prevShowTranscriptRef.current;
		prevShowTranscriptRef.current = true;
		const delay = justOpened ? 50 : 0;
		const id = setTimeout(() => {
			activeSegRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, delay);
		return () => clearTimeout(id);
	}, [
		showTranscript,
		activeLine?.chapterIndex,
		activeLine?.segmentIndex,
		activeLine?.lineIndex,
	]);

	useEffect(() => {
		if (!showTranscript) prevShowTranscriptRef.current = false;
	}, [showTranscript]);

	// Setup gesture handlers
	const { gestureState, bindArtworkDrag, bindVerticalDrag } = usePlayerGestures(
		{
			onMinimize: () => handleMinimizeWithAnimation(),
			onNextChapter: () => {
				if (activeChapterIndex < chapters.length - 1) {
					getPlayerActions().goToChapter(activeChapterIndex + 1);
				}
			},
			onPrevChapter: () => {
				if (activeChapterIndex > 0) {
					getPlayerActions().goToChapter(activeChapterIndex - 1);
				}
			},
			isFullscreen: true,
		},
	);

	const totalDuration = Number.isFinite(duration) ? duration : 0;

	// Get current chapter data for sharing
	const currentChapterData = podcastChapters[activeChapterIndex];
	// Fallback to first chapter if no active chapter
	const shareChapterData = currentChapterData || podcastChapters[0];
	// Use centralized selector for progress to keep behavior consistent.
	const progress = playerSelectors.getProgressPercentage(
		usePlayerStore.getState(),
	);
	const remainingTime = totalDuration - currentTime;

	// Handle play/pause toggle for mobile
	const handlePlayPause = () => {
		if (playerRef.current) {
			if (isPaused) {
				console.log("  - Calling play()");
				playerRef.current.play();
			} else {
				console.log("  - Calling pause()");
				playerRef.current.pause();
			}
		} else {
			console.error("  - playerRef.current is null!");
		}
	};

	// Progress bar change
	const handleProgressChange = (value: number[]) => {
		if (playerRef.current && totalDuration > 0) {
			const newTime = (value[0] / 100) * totalDuration;
			playerRef.current.currentTime = newTime;
		}
	};

	// Handle playback speed change
	const handleSpeedChange = (value: number[]) => {
		const newSpeed = value[0];
		if (playerRef.current) {
			playerRef.current.playbackRate = newSpeed;
		}
	};

	// Handle preset speed change (for buttons)
	const handlePresetSpeedChange = (speed: number) => {
		if (playerRef.current) {
			playerRef.current.playbackRate = speed;
		}
	};

	// Handle increment/decrement speed
	const handleIncrementSpeed = () => {
		const newSpeed = Math.min(playbackSpeed + 0.2, 2.6);
		if (playerRef.current) {
			playerRef.current.playbackRate = Number(newSpeed.toFixed(1));
		}
	};

	const handleDecrementSpeed = () => {
		const newSpeed = Math.max(playbackSpeed - 0.2, 0.5);
		if (playerRef.current) {
			playerRef.current.playbackRate = Number(newSpeed.toFixed(1));
		}
	};

	// Handle minimize with animation
	const handleMinimizeWithAnimation = () => {
		setIsExiting(true);
		// Wait for animation to complete before actually minimizing
		setTimeout(() => {
			onMinimize();
		}, 300); // Match animation duration
	};

	// Get artwork URL or fallback
	const artworkUrl = currentMedia?.artwork || "/podhive-orange.png";

	// Handle chat button click - check authentication and workspace availability first
	const handleChatClick = () => {
		if (!podWorkspaceId) {
			toast.error(t("chat.notAvailableForShared"));
			return;
		}
		if (!isAuthenticated) {
			setShowRegisterPrompt(true);
		} else {
			onOpenChat();
		}
	};

	return (
		<div
			className={`fixed inset-0 z-[60] bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white flex flex-col transition-transform duration-300 ${
				isExiting
					? "translate-y-full"
					: "translate-y-0 animate-in slide-in-from-bottom"
			}`}
			style={{
				paddingTop: "env(safe-area-inset-top)",
				paddingBottom: "env(safe-area-inset-bottom)",
				transform: gestureState.isDragging
					? `translateY(${gestureState.y}px)`
					: undefined,
				opacity: gestureState.isDragging ? gestureState.opacity : 1,
			}}
		>
			{/* Top navigation bar */}
			<div
				className="flex items-center justify-between px-4 pt-4 pb-2"
				{...bindVerticalDrag()}
			>
				<Button
					variant="ghost"
					size="icon"
					onClick={handleMinimizeWithAnimation}
					className="h-10 w-10 rounded-full hover:bg-white/10"
				>
					<ChevronDown className="h-6 w-6" />
				</Button>

				{/* Share button - choose between chapter or whole podcast */}
				{(shareChapterData?.id || podcastId) && (
					<div className="[&>button]:!h-10 [&>button]:!w-10 [&>button]:!bg-white/10 [&>button]:!rounded-full [&>button]:hover:!bg-white/20">
						<ShareButton
							chapterId={shareChapterData?.id}
							chapterTitle={shareChapterData?.title}
							podcastId={podcastId || undefined}
							podcastTitle={currentMedia?.subtitle}
							variant="ghost"
							size="icon"
						/>
					</div>
				)}
			</div>

			{/* Main content area: artwork OR inline transcript */}
			{showTranscript ? (
				/* ── Inline transcript view ── */
				<div className="flex-1 min-h-0 flex flex-col px-4 pt-2 pb-1 overflow-hidden">
					{/* Translation chips + globe button */}
					{transcript &&
						transcript.chapters.some((c) => c.segments.length > 0) && (
							<div className="flex items-center gap-1.5 flex-wrap mb-2 shrink-0">
								{availableTranslations.map((lang) => {
									const isActive =
										isDualLanguage && translationLangCode === lang;
									return (
										<button
											key={lang}
											type="button"
											onClick={() => {
												const { setIsDualLanguage, setTranslationLangCode } =
													getTranscriptActions();
												if (isActive) {
													setIsDualLanguage(false);
												} else {
													setTranslationLangCode(lang);
													setIsDualLanguage(true);
												}
											}}
											className={[
												"flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors",
												isActive
													? "border-orange-400/40 bg-orange-400/10 text-orange-300"
													: "border-white/20 text-gray-400",
											].join(" ")}
										>
											{isActive && (
												<CheckCircle2 className="h-3 w-3 shrink-0" />
											)}
											<span>
												{originalLang}
												<span className="mx-0.5 opacity-50">→</span>
												{lang.slice(0, 2).toUpperCase()}
											</span>
										</button>
									);
								})}
								{/* Add Translation button */}
								<button
									type="button"
									onClick={() => setShowMobileTranslateControls((v) => !v)}
									className={[
										"flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border transition-colors",
										showMobileTranslateControls
											? "border-orange-400/40 bg-orange-400/10 text-orange-300"
											: "border-white/20 text-gray-400",
									].join(" ")}
								>
									{showMobileTranslateControls ? (
										<X className="h-3 w-3 shrink-0" />
									) : (
										<Globe className="h-3 w-3 shrink-0" />
									)}
									<span>
										{isTranslatingLang
											? t("transcript.translation.generating")
											: t("transcript.translation.addLanguage")}
									</span>
									{isTranslatingLang && (
										<Loader2 className="h-3 w-3 animate-spin shrink-0" />
									)}
								</button>
							</div>
						)}

					{/* Expandable translation controls */}
					{showMobileTranslateControls &&
						transcript &&
						transcript.chapters.some((c) => c.segments.length > 0) && (
							<div className="mb-2 p-3 rounded-xl border border-white/10 bg-white/5 space-y-2 shrink-0">
								<div className="flex items-center gap-2">
									<Select
										value={mobileGenerateLang}
										onValueChange={setMobileGenerateLang}
										disabled={!!isTranslatingLang}
									>
										<SelectTrigger className="h-10 text-sm flex-1 bg-white/10 border-white/20 text-white">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{TRANSLATABLE_LANGUAGES.filter(
												(l) => l.code !== podcastLang,
											).map((l) => (
												<SelectItem
													key={l.code}
													value={l.code}
													className="text-sm"
												>
													{l.label}
													{availableTranslations.includes(l.code) && (
														<span className="ml-1.5 text-[11px] text-green-500 font-medium">
															✓
														</span>
													)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<Button
										className="h-8 px-4 shrink-0 bg-orange-500 hover:bg-orange-600 text-white"
										disabled={!!isTranslatingLang}
										onClick={async () => {
											if (!podcastId || !transcript || isTranslatingLang)
												return;
											// Guard: already fully translated — just switch to it
											const segs = transcript.chapters.flatMap(
												(c) => c.segments,
											);
											if (
												segs.length > 0 &&
												segs.every(
													(s) => !!s.translations?.[mobileGenerateLang],
												)
											) {
												getTranscriptActions().setTranslationLangCode(
													mobileGenerateLang,
												);
												getTranscriptActions().setIsDualLanguage(true);
												setShowMobileTranslateControls(false);
												return;
											}
											const ids = transcript.chapters
												.filter((c) => c.segments.length > 0)
												.map((c) => c.id);
											getTranscriptActions().startTranslating(
												podcastId,
												mobileGenerateLang,
											);
											try {
												await PodcastService.triggerPodcastTranslation(
													podcastId,
													ids,
													mobileGenerateLang,
													podcastLang,
												);
											} catch {
												getTranscriptActions().stopTranslating(podcastId);
											}
										}}
									>
										{isTranslatingLang ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											t("transcript.translation.generate")
										)}
									</Button>
								</div>
								{isTranslatingLang && (
									<p className="text-xs text-orange-300/70 flex items-center gap-1.5">
										<Loader2 className="h-3 w-3 animate-spin shrink-0" />
										{t("transcript.translation.translatingTo", {
											lang:
												TRANSLATABLE_LANGUAGES.find(
													(l) => l.code === isTranslatingLang,
												)?.label ?? isTranslatingLang,
										})}
									</p>
								)}
							</div>
						)}

					{transcriptLoading && (
						<div className="flex-1 overflow-hidden space-y-4 pt-1">
							{[5, 4].map((lineCount, ci) => (
								<div key={ci}>
									{/* Chapter heading */}
									<div className="flex items-center gap-2 mb-2">
										<Skeleton className="h-5 w-5 rounded-full shrink-0 bg-white/10" />
										<Skeleton className="h-3 w-28 bg-white/10" />
									</div>
									{/* Lines */}
									{Array.from({ length: lineCount }).map((_, li) => {
										const widths = [
											"w-full",
											"w-4/5",
											"w-3/4",
											"w-full",
											"w-2/3",
										];
										const w = widths[(ci * 10 + li) % widths.length];
										return (
											<div key={li} className="px-3 py-1.5">
												{isDualLanguage ? (
													<div className="space-y-1.5">
														<Skeleton className={`h-3.5 ${w} bg-white/10`} />
														<Skeleton
															className={`h-3 ${widths[(ci * 10 + li + 2) % widths.length]} bg-white/[0.06]`}
														/>
													</div>
												) : (
													<Skeleton className={`h-3.5 ${w} bg-white/10`} />
												)}
											</div>
										);
									})}
								</div>
							))}
						</div>
					)}

					{!transcriptLoading &&
						(!transcript ||
							transcript.chapters.every((c) => c.segments.length === 0)) && (
							<div className="flex-1 flex items-center justify-center text-gray-400 text-sm text-center px-8">
								{t("transcript.notAvailable")}
							</div>
						)}

					{/* Scrollable lines */}
					{transcript && (
						<div className="flex-1 overflow-y-auto space-y-0.5 pb-2">
							{transcript.chapters.map((chapter, ci) => (
								<MobileTranscriptChapter
									key={chapter.id}
									chapter={chapter}
									chapterIndex={ci}
									activeSegmentIndex={
										activeLine?.chapterIndex === ci
											? activeLine.segmentIndex
											: null
									}
									activeLineIndex={
										activeLine?.chapterIndex === ci
											? activeLine.lineIndex
											: null
									}
									activeLineRef={activeSegRef}
									isDualLanguage={isDualLanguage}
									isTranslating={!!isTranslatingLang}
									translationLangCode={translationLangCode}
								/>
							))}
						</div>
					)}
				</div>
			) : (
				/* ── Artwork view ── */
				<>
					<div
						className="flex items-center justify-center px-8 py-4 touch-none overflow-hidden"
						style={{
							flexShrink: 1,
							flexGrow: 1,
							flexBasis: 0,
							maxHeight: "calc(100vh - 380px)",
							minHeight: "100px",
						}}
						{...bindArtworkDrag()}
					>
						<div className="relative w-full h-full flex items-center justify-center">
							<img
								src={artworkUrl}
								alt={currentMedia?.title || "Podcast artwork"}
								className="max-w-full max-h-full w-auto h-auto aspect-square object-cover rounded-2xl shadow-2xl"
								style={{
									transform:
										gestureState.isDragging && gestureState.x !== 0
											? `translateX(${gestureState.x}px) scale(${1 - Math.abs(gestureState.x) / 1000})`
											: undefined,
									transition: gestureState.isDragging
										? "none"
										: "transform 0.3s ease-out",
								}}
							/>
							{gestureState.isDragging && gestureState.x < -50 && (
								<div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full px-4 text-white/60 text-sm whitespace-nowrap">
									Next Chapter →
								</div>
							)}
							{gestureState.isDragging && gestureState.x > 50 && (
								<div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full px-4 text-white/60 text-sm whitespace-nowrap">
									← Previous Chapter
								</div>
							)}
						</div>
					</div>

					{/* Chapter indicator */}
					{currentChapter && (
						<div className="px-8 py-4 text-center flex-shrink-0">
							<div className="flex items-center justify-center gap-2 mb-2">
								<List className="h-4 w-4 text-gray-400" />
								<p className="text-sm text-gray-400">
									Chapter {activeChapterIndex + 1} of {chapters.length}
								</p>
							</div>
							<h2 className="text-xl font-bold truncate px-4">
								{currentChapter.text || "Current Chapter"}
							</h2>
						</div>
					)}

					{/* Podcast title fallback */}
					{!currentChapter && currentMedia?.title && (
						<div className="px-8 py-4 text-center flex-shrink-0">
							<h2 className="text-xl font-bold truncate px-4">
								{currentMedia.title}
							</h2>
						</div>
					)}
				</>
			)}

			{/* Progress bar and time */}
			<div className="px-8 pt-5 space-y-2 flex-shrink-0">
				<Slider
					value={[progress]}
					onValueChange={handleProgressChange}
					max={100}
					step={0.1}
					className="w-full"
				/>
				<div className="flex items-center justify-between text-sm text-gray-400">
					<span>{formatTime(currentTime)}</span>
					{/* <span>{formatTime(totalDuration)} verbleibend</span> */}
					<span>- {formatTime(remainingTime)}</span>
				</div>
			</div>

			{/* Playback controls */}
			<div className="px-8 py-5 flex-shrink-0">
				<PlayerControls
					isPaused={isPaused}
					variant="mobile"
					onPlayPause={handlePlayPause}
				/>
			</div>

			{/* Bottom utility bar */}
			<div className="px-8 pb-8 pt-4 flex-shrink-0">
				<div className="flex items-center justify-around">
					{/* Playback speed */}
					<button
						type="button"
						onClick={() => setIsSpeedPopupOpen(true)}
						className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
					>
						<div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
							<Gauge className="h-5 w-5" />
						</div>
						<span className="text-xs text-gray-400">{playbackSpeed}x</span>
					</button>

					{/* Chapters list */}
					<button
						type="button"
						onClick={onOpenChapters}
						className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
					>
						<div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
							<List className="h-5 w-5" />
						</div>
						<span className="text-xs text-gray-400">
							{t("player.chapters")}
						</span>
					</button>

					{/* Artwork / Transcript toggle */}
					<button
						type="button"
						onClick={() => {
							setShowTranscript((v) => {
								if (!v) {
									AnalyticsService.track("transcript_opened", {
										platform: "mobile",
										podcast_id: podcastId ?? null,
									});
								}
								return !v;
							});
						}}
						className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
					>
						<div
							className={[
								"h-10 w-10 rounded-full flex items-center justify-center transition-colors",
								showTranscript
									? "bg-primary/30 ring-1 ring-primary"
									: "bg-white/10",
							].join(" ")}
						>
							{showTranscript ? (
								<Image className="h-5 w-5 text-primary" />
							) : (
								<FileText className="h-5 w-5" />
							)}
						</div>
						<span
							className={[
								"text-xs transition-colors",
								showTranscript ? "text-primary" : "text-gray-400",
							].join(" ")}
						>
							{showTranscript ? t("player.artwork") : t("transcript.title")}
						</span>
					</button>

					{/* Chat */}
					<button
						type="button"
						onClick={handleChatClick}
						className="flex flex-col items-center gap-1 hover:opacity-80 transition-opacity"
					>
						<div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center">
							<MessageSquare className="h-5 w-5" />
						</div>
						<span className="text-xs text-gray-400">Chat</span>
					</button>
				</div>
			</div>

			{/* Register Prompt Dialog */}
			<RegisterPromptDialog
				open={showRegisterPrompt}
				onOpenChange={setShowRegisterPrompt}
				title={t("chat.registerPrompt.title")}
				description={t("chat.registerPrompt.description")}
				customMessage={t("chat.registerPrompt.message")}
			/>

			{/* Playback Speed Popup */}
			{isSpeedPopupOpen && (
				<button
					type="button"
					className="fixed inset-0 z-[75] bg-black/50 flex items-end"
					onClick={() => setIsSpeedPopupOpen(false)}
				>
					<div
						className="w-full bg-gray-800 rounded-t-3xl p-6 space-y-6 animate-in slide-in-from-bottom-4 touch-none"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
						role="dialog"
						aria-label="Playback speed settings"
						tabIndex={-1}
						style={{
							paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)",
						}}
						{...bindVerticalDrag()}
					>
						{/* Header */}
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-semibold text-white">
								{t("player.playbackSpeed")}
							</h3>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => setIsSpeedPopupOpen(false)}
								className="h-8 w-8 rounded-full hover:bg-white/10"
							>
								<X className="h-5 w-5" />
							</Button>
						</div>

						{/* Speed Display */}
						<div className="text-center">
							<div className="text-4xl font-bold text-white mb-2">
								{playbackSpeed.toFixed(2).replace(/\.?0+$/, "")}x
							</div>
							<p className="text-sm text-gray-400">
								{playbackSpeed < 1
									? t("player.slower")
									: playbackSpeed > 1
										? t("player.faster")
										: t("player.normal")}
							</p>
						</div>

						{/* Speed Slider with increment/decrement buttons */}
						<div className="space-y-4">
							<div className="flex items-center gap-4">
								{/* Decrement button */}
								<Button
									variant="outline"
									onClick={handleDecrementSpeed}
									disabled={playbackSpeed <= 0.5}
									size="iconLg"
									className="bg-transparent border-gray-600 rounded-full"
								>
									<Minus className="h-6 w-6" />
								</Button>

								{/* Slider */}
								<div className="flex-1">
									<Slider
										value={[playbackSpeed]}
										onValueChange={handleSpeedChange}
										min={0.5}
										max={2.6}
										step={0.2}
										className="w-full"
									/>
								</div>

								{/* Increment button */}
								<Button
									variant="outline"
									onClick={handleIncrementSpeed}
									disabled={playbackSpeed >= 2.6}
									size="iconLg"
									className="bg-transparent border-gray-600 rounded-full"
								>
									<Plus className="h-6 w-6" />
								</Button>
							</div>
						</div>

						{/* Quick Presets - All standard options */}
						<div className="grid grid-cols-6 gap-2">
							{PLAYBACK_SPEED_OPTIONS.map((speed) => (
								<Button
									key={speed}
									variant="outline"
									onClick={() => handlePresetSpeedChange(speed)}
									size="iconLg"
									className="bg-white/10 border-gray-600 rounded-md w-full active:scale-95"
								>
									<div className="flex-col items-center justify-center">
										{speed}x
										{speed === 1.0 && (
											<span className="block text-[7px] mt-0.5 opacity-80 uppercase tracking-wider">
												Standard
											</span>
										)}
									</div>
								</Button>
							))}
						</div>
					</div>
				</button>
			)}
		</div>
	);
}
